/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import ReactCrop from "react-easy-crop";
import {
  uploadBannerImage,
  fetchBannerImage,
  deleteBannerImage,
  vendorFetchSubscriptionApi,
  editBannerImage,
} from "../../../../Services/Vendor/VendorApi/VendorApi";
import { useDispatch, useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import { useDropzone } from "react-dropzone";

// Utility function to crop the image and return a Data URL
const getCroppedImg = (imageSrc, pixelCrop) => {
  const image = new Image();
  image.src = imageSrc;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Setting canvas size
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  return new Promise((resolve) => {
    image.onload = () => {
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      // Resolve with the base64 image
      resolve(canvas.toDataURL("image/jpeg"));
    };
  });
};

const Dropzone = ({ handleFileChange }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [],
    },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      const event = { target: { files: [file] } };
      handleFileChange(event);
    },
  });

  return (
    <div {...getRootProps()} className="upload-box">
      <input {...getInputProps()} />
      <div>
        {isDragActive ? (
          <p>Drop the image here</p>
        ) : (
          <div className="d-flex flex-column align-items-center">
            <FileUploadOutlinedIcon fontSize="large" color="action" />
            <p>Drag & drop an image here, or click to browse</p>
            <label htmlFor="fileInput" className="upload-btn-1">
              Select from device
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

function UploadBannerModal({ id }) {
  const dispatch = useDispatch();
  const userId = localStorage.getItem("user_id");
  const [show, setShow] = useState(false); // For the upload modal
  const [showCropModal, setShowCropModal] = useState(false); // For the crop modal
  const [image, setImage] = useState(null); // Image to crop
  const [crop, setCrop] = useState({ x: 0, y: 0 }); // Crop position
  const [zoom, setZoom] = useState(1); // Zoom level for crop tool
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null); // Crop area in pixels
  const [selectedImage, setSelectedImage] = useState(null); // Store only one selected image
  const [imageUrl, setImageUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [editedUrls, setEditedUrls] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const { data } = useSelector((state) => state.fetchBannerImage);

  const handleUrlChange = (index, newUrl) => {
    setEditedUrls((prev) => ({
      ...prev,
      [index]: newUrl,
    }));
  };

  const handleClose = () => setShow(false);
  const handleCloseCropModal = () => {
    setShowCropModal(false);
    setImage(null);
    setImageUrl("");
    setUrlError("");
    setShow(true); // Reopen the upload modal
  };
  const handleShow = () => setShow(true);

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result); // Set the selected image
      setShow(false); // Close upload modal
      setShowCropModal(true); // Then show crop modal
    };
    if (file) reader.readAsDataURL(file);
  };

  // Handle cropping area change
  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels); // Update cropped area in pixels
  };

  // Crop the image when user clicks Save

  const handleSave = async () => {
    if (image && croppedAreaPixels) {
      if (!imageUrl.trim()) {
        setUrlError("URL is required.");
        return; // Don't proceed if URL is empty
      }

      const croppedImage = await getCroppedImg(image, croppedAreaPixels);

      // Create a new FormData object
      const formData = new FormData();

      const byteString = atob(croppedImage.split(",")[1]);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: "image/jpeg" });

      formData.append("ads_banner_image", blob, `${new Date().getTime()}.jpg`);
      formData.append("ads_banner_url", imageUrl);
      formData.append("vendor_subscriber", id);
      formData.append("user_id", userId);
      formData.append("is_selected", true);

      try {
        setIsSaving(true);
        // Dispatch the API call and wait for the result
        const resultAction = await dispatch(uploadBannerImage(formData));

        // Check if the action was successful (fulfilled)
        if (uploadBannerImage.fulfilled.match(resultAction)) {
          // If successful, handle the response
          toast.success("Image uploaded successfully");
          dispatch(fetchBannerImage(userId));
          dispatch(vendorFetchSubscriptionApi(userId));

          // Close all modals and reset states
          setShowCropModal(false);
          setShow(false);
          setImage(null);
          setImageUrl("");
          setUrlError("");
        } else {
          // If failed, handle the error
          console.error("Upload failed:", resultAction.payload);
        }
        setIsSaving(false);
      } catch (error) {
        setIsSaving(false);
        console.error("Error in handleSave:", error);
      }
    }
  };

  const handleImageSelect = (index) => {
    setSelectedImage(index === selectedImage ? null : index);

    // Prefill the edited URL if not already set
    if (!editedUrls[index]) {
      setEditedUrls((prev) => ({
        ...prev,
        [index]: data?.data?.[index]?.ads_banner_url || "",
      }));
    }
  };

  const handleSelectedImageUpload = async () => {
    if (selectedImage === undefined || !data?.data[selectedImage]) {
      return;
    }

    const selectedImg = data?.data[selectedImage];

    const finalUrl = editedUrls[selectedImage] ?? imageUrl;

    try {
      setIsSaving(true);

      // Convert image URL to Blob
      // const response = await fetch(selectedImg.ads_banner_image, {
      //   mode: "cors",
      // });
      // const blob = await response.blob();
      // const file = new File(
      //   [blob],
      //   `uploaded_image${new Date().getTime()}.jpg`,
      //   { type: blob.type }
      // );

      const formData = new FormData();
      formData.append("ads_banner_image", selectedImg?.ads_banner_image); // Append file instead of URL
      formData.append("ads_banner_url", finalUrl);
      formData.append("vendor_subscriber_id", id);
      formData.append("advertisement_library_id", selectedImg?.id);
      formData.append("is_selected", true);

      const resultAction = await dispatch(editBannerImage(formData));

      if (uploadBannerImage.fulfilled.match(resultAction)) {
        dispatch(fetchBannerImage(userId));
        dispatch(vendorFetchSubscriptionApi(userId));
        setSelectedImage(null);
        setImageUrl("");
        handleClose();
      } else {
        console.error("Upload failed:", resultAction.payload);
      }
    } catch (error) {
      console.error("Error in handleSelectedImageUpload:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setIsSaving(true);
      const resultAction = await dispatch(deleteBannerImage(id));

      if (deleteBannerImage.fulfilled.match(resultAction)) {
        // Refresh uploaded images
        dispatch(fetchBannerImage(userId));
        dispatch(vendorFetchSubscriptionApi(userId));

        setSelectedImage(null); // Reset selected image
        setImageUrl("");
      } else {
        console.error("Delete failed:", resultAction.payload);
      }
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      console.error("Error in handleDelete:", error);
    }
  };

  const handleDeleteBanner = (id) => {
    toast(
      <div className="text-center">
        <p className="mb-2">
          Are you sure you want to delete this banner? This action will remove
          the banner from all cities where it is currently set.
        </p>
        <div className="d-flex justify-content-center gap-2">
          <button
            onClick={() => {
              handleDelete(id);
              toast.dismiss();
            }}
            className="bttn-close"
          >
            Yes
          </button>
          <button onClick={() => toast.dismiss()} className="bttn-upload">
            No
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        className: "custom-toast",
        closeButton: false,
      }
    );
  };

  return (
    <>
      <a style={{ cursor: "pointer" }} title="Upload Banner">
        <div className="upload-btn-icon" onClick={handleShow}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 64 64"
            style={{
              enableBackground: "new 0 0 512 512",
            }}
          >
            <g>
              <g data-name="Layer 51">
                <path
                  d="M57.47 38.6a2 2 0 0 0-2 2v6.83a6.07 6.07 0 0 1-6.07 6.07H14.6a6.07 6.07 0 0 1-6.07-6.07V40.6a2 2 0 1 0-4 0v6.83A10.08 10.08 0 0 0 14.6 57.5h34.8a10.08 10.08 0 0 0 10.07-10.07V40.6a2 2 0 0 0-2-2z"
                  fill="#000000"
                />
                <path
                  d="m22 21.3 8-8V43a2 2 0 0 0 4 0V13.33l8 8a2 2 0 0 0 2.83-2.83L33.42 7.09a2.16 2.16 0 0 0-1-.55 2.06 2.06 0 0 0-1.81.55L19.2 18.47A2 2 0 0 0 22 21.3z"
                  fill="#000000"
                />
              </g>
            </g>
          </svg>
        </div>
      </a>

      {/* Upload Modal */}
      <Modal show={show} onHide={handleClose} centered>
        {/* <Modal.Header closeButton>
          <Modal.Title>Select an Image</Modal.Title>
        </Modal.Header> */}
        <Modal.Body>
          <p className="fw-light">
            You can upload a new image or select from the gallery.
          </p>
          <Tabs
            defaultActiveKey="upload"
            id="noanim-tab-example"
            className="mb-3"
          >
            <Tab
              eventKey="upload"
              title={
                <>
                  <FileUploadOutlinedIcon
                    titleAccess="Upload"
                    sx={{ color: "#e55426" }}
                  />
                  <span className="ms-2 upload-tabs-span">Upload</span>
                </>
              }
            >
              <Form>
                <div className="upload-box">
                  <Dropzone handleFileChange={handleFileChange} />
                </div>
              </Form>
            </Tab>
            <Tab
              eventKey="gallery"
              title={
                <>
                  <CollectionsRoundedIcon
                    titleAccess="Gallery"
                    sx={{ color: "#e55426" }}
                  />
                  <span className="ms-2 upload-tabs-span">Gallery</span>
                </>
              }
            >
              {/* Display previously uploaded images */}
              <div className="uploaded-images">
                <div className="uploaded-images d-flex flex-column gap-4">
                  <p></p>
                  {data?.data?.length > 0 ? (
                    data?.data?.map((imgObj, index) => (
                      <div
                        key={imgObj?.ads_banner_url + index}
                        className="image-preview d-flex gap-2 position-relative"
                      >
                        <input
                          type="checkbox"
                          checked={selectedImage === index}
                          onChange={() => handleImageSelect(index)}
                          style={{ marginRight: "10px", display: "none" }}
                        />
                        <div
                          onClick={() => handleImageSelect(index)}
                          style={{
                            width: "100%",
                            height: "100%",
                            cursor: "pointer",
                            border:
                              selectedImage === index
                                ? "4px solid #e55426"
                                : "none",
                            borderRadius: "4px",
                          }}
                        >
                          <img
                            src={imgObj.ads_banner_image} // Use imgObj.image
                            alt={`uploaded-${index}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <input
                            type="text"
                            value={editedUrls[index] ?? imgObj.ads_banner_url}
                            onChange={(e) =>
                              handleUrlChange(index, e.target.value)
                            }
                            style={{
                              width: "100%",
                              marginTop: "8px",
                              padding: "5px",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                            }}
                          />
                        </div>
                        <div className="position-absolute delete-btn">
                          <IconButton
                            aria-label="delete"
                            size="medium"
                            style={{ color: "#e55426" }}
                            onClick={() => {
                              handleDeleteBanner(imgObj?.id);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              version="1.1"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                              width="30"
                              height="30"
                              viewBox="0 0 512 512"
                              style={{ enableBackground: "new 0 0 512 512" }}
                              xmlSpace="preserve"
                            >
                              <g>
                                <path
                                  fill="#da4538"
                                  fillRule="evenodd"
                                  d="M256 512c140.997 0 256-115.003 256-256S396.997 0 256 0 0 115.003 0 256s115.003 256 256 256z"
                                  clipRule="evenodd"
                                  opacity="1"
                                ></path>
                                <path
                                  fill="#ffffff"
                                  d="M366.313 171.409H145.687a4.539 4.539 0 0 0-3.291 1.415 4.541 4.541 0 0 0-1.246 3.369l11.593 223.69c.513 9.951 8.718 17.747 18.688 17.747h169.137c9.971 0 18.176-7.796 18.688-17.747l11.593-223.69a4.541 4.541 0 0 0-1.246-3.369 4.535 4.535 0 0 0-3.29-1.415zm-182.334 45.095c-.084-3.044 1.026-5.946 3.129-8.166s4.94-3.492 7.984-3.583c6.335-.13 11.593 4.842 11.775 11.126l4.213 157.024a11.324 11.324 0 0 1-3.129 8.173 11.439 11.439 0 0 1-7.997 3.577h-.337c-2.928 0-5.7-1.11-7.848-3.148a11.387 11.387 0 0 1-3.57-7.978zm60.577 156.537V215.997c0-6.316 5.135-11.451 11.444-11.451s11.444 5.135 11.444 11.451v157.044c0 6.322-5.135 11.464-11.444 11.464s-11.444-5.141-11.444-11.464zm79.245.474a11.393 11.393 0 0 1-3.57 7.991c-2.148 2.038-4.92 3.148-7.848 3.148h-.357a11.379 11.379 0 0 1-7.978-3.583 11.316 11.316 0 0 1-3.129-8.179l4.213-156.998c.182-6.29 5.401-11.295 11.697-11.139h.078c3.044.091 5.881 1.363 7.984 3.583s3.213 5.122 3.129 8.166zM389 136.414v12.048H123v-12.048c0-6.647 5.407-12.054 12.054-12.054h87.496a4.547 4.547 0 0 0 4.518-4.051C228.685 105.522 241.122 94.37 256 94.37s27.315 11.152 28.932 25.939a4.547 4.547 0 0 0 4.518 4.051h87.496c6.647 0 12.054 5.407 12.054 12.054z"
                                  opacity="1"
                                ></path>
                              </g>
                            </svg>
                          </IconButton>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No images uploaded yet.</p>
                  )}
                </div>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <button className="bttn-close" onClick={handleClose}>
            Close
          </button>
          <button
            className="bttn-upload"
            onClick={handleSelectedImageUpload}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Crop Modal */}
      <Modal show={showCropModal} onHide={handleCloseCropModal}>
        <Modal.Header closeButton>
          <Modal.Title>Crop Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ position: "relative", width: "100%", height: "300px" }}>
            {image && (
              <ReactCrop
                image={image}
                crop={crop}
                zoom={zoom}
                minWidth={1600}
                minHeight={400}
                aspect={1600 / 400}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
              />
            )}
          </div>
          <div style={{ marginTop: "20px" }}>
            <label htmlFor="urlInput">Image URL:</label>
            <input
              type="text"
              id="urlInput"
              className="form-control"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Enter image URL"
            />
            {urlError && (
              <p style={{ color: "red", marginTop: "10px" }}>{urlError}</p>
            )}{" "}
            {/* Error message */}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="bttn-close" onClick={handleCloseCropModal}>
            Close
          </button>
          <button
            className="bttn-upload"
            onClick={handleSave}
            disabled={isSaving}
          >
            Save Cropped Image
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UploadBannerModal;
